import { useState } from "react";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { useStep, useSubmitGate, useTick } from "@/app/queries";
import { useMe } from "@/app/session";
import { NotFound, SkeletonRows, StateCard, Toast } from "@/ui/states";
import { CodeSection, MistakesBlock, NotesBlock, QuizBlock } from "./step/Blocks";
import { GateCard } from "./step/Gate";
import { MobileStep } from "./step/MobileStep";
import { Practice } from "./step/Practice";
import { Primary } from "./step/Primary";
import { Resources } from "./step/Resources";
import { StepAside } from "./step/StepAside";
import { StepToc } from "./step/StepToc";
import { Stuck } from "./step/Stuck";
import { StepFeedbackBlock } from "./step/Feedback";
import { errorInfo, useActiveSection, useIsMobile, type Section, type TickVars } from "./step/model";

const SECTIONS: Section[] = ["res", "primary", "quiz", "mistakes", "code", "notes", "practice", "stuck", "gate"];

/** Экран шага (S4, вариант 4a; S8 при < md). Отметки — оптимистично,
 *  при ошибке — тост с «повторить»; гейт сдаётся кнопкой с причиной. */
export function StepPage({ id }: { id: string }) {
  const me = useMe();
  const step = useStep(id);
  const tickMut = useTick(id);
  const submit = useSubmitGate(id);
  const mobile = useIsMobile();
  const active = useActiveSection(SECTIONS, step.status === "success" && !mobile);
  const [failedTick, setFailedTick] = useState<TickVars | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const tick = (vars: TickVars) => {
    setFailedTick(null);
    tickMut.mutate(vars, { onError: () => setFailedTick(vars) });
  };
  const onSubmit = () => {
    setSubmitError(null);
    submit.mutate(undefined, { onError: (e) => setSubmitError(errorInfo(e).message) });
  };

  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;

  if (step.status === "pending") {
    return (
      <Shell nav user={user}>
        <SkeletonRows rows={6} />
      </Shell>
    );
  }
  if (step.status === "error") {
    const e = errorInfo(step.error);
    return (
      <Shell nav user={user}>
        {e.code === "step_locked" || e.status === 403 ? (
          <StateCard code="LOCKED" where="шаг маршрута" title="Шаг ещё закрыт" body={e.message} primary={{ label: "вернуться к маршруту", href: "/" }} />
        ) : e.status === 404 ? (
          <NotFound />
        ) : (
          <StateCard
            code="ERR_NETWORK"
            where="загрузка шага"
            tone="warn"
            title="Нет связи с сервером"
            body="Шаг не смог загрузиться. Отметки, поставленные раньше, сохранены — обновите страницу, когда связь вернётся."
            primary={{ label: "повторить сейчас", onClick: () => void step.refetch() }}
          />
        )}
      </Shell>
    );
  }
  const view = step.data;

  const toasts = (
    <div className={`pointer-events-none fixed z-30 flex flex-col gap-2 ${mobile ? "inset-x-4 bottom-[160px]" : "bottom-6 right-6 w-[min(520px,calc(100vw-48px))]"}`} aria-live="polite">
      {failedTick && (
        <div className="pointer-events-auto">
          <Toast
            tone="err"
            title="Не удалось сохранить отметку"
            body="Отметка вернётся в прежнее состояние. Проверьте связь и попробуйте ещё раз."
            action={{ label: "повторить", onClick: () => tick(failedTick) }}
          />
        </div>
      )}
      {submitError && (
        <div className="pointer-events-auto">
          <Toast tone="err" title="Не удалось сдать гейт" body={submitError} action={{ label: "повторить", onClick: onSubmit }} />
        </div>
      )}
    </div>
  );

  if (mobile) {
    return (
      <Shell nav user={user} wide>
        <MobileStep view={view} tick={tick} onSubmit={onSubmit} pending={submit.isPending} />
        {toasts}
      </Shell>
    );
  }

  return (
    <Shell nav user={user} wide meta={`маршрут / ${view.track.toLowerCase()} / шаг ${view.step_index}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[232px_minmax(0,1fr)_312px]">
        <nav aria-label="оглавление шага" className="border-b border-bd px-4 py-5 lg:sticky lg:top-[57px] lg:self-start lg:border-b-0 lg:border-r">
          <StepToc view={view} active={active} />
        </nav>

        <div className="flex flex-col gap-[26px] px-4 pb-10 pt-6 md:px-7">
          <header>
            <div className="mb-2 font-mono text-[11.5px] text-accdim">
              {view.track.toLowerCase()} · шаг {view.step_index} из {view.track_total}
            </div>
            <h1 className="m-0 mb-2.5 font-mono text-[30px] font-bold leading-[1.1] tracking-[-.02em]">{view.title}</h1>
            {view.content.intro && <p className="m-0 max-w-[680px] text-pretty text-[14px] text-ink2">{view.content.intro}</p>}
          </header>
          <Resources view={view} tick={tick} />
          <Primary view={view} tick={tick} />
          <QuizBlock view={view} tick={tick} />
          <MistakesBlock view={view} />
          <CodeSection view={view} />
          <NotesBlock view={view} />
          <Practice view={view} tick={tick} />
          <Stuck view={view} />
          <GateCard view={view} onSubmit={onSubmit} pending={submit.isPending} />
          <StepFeedbackBlock stepId={view.id} />
        </div>

        <aside className="border-t border-bd px-[18px] py-5 lg:sticky lg:top-[57px] lg:self-start lg:border-l lg:border-t-0">
          <StepAside view={view} />
        </aside>
      </div>
      {toasts}
    </Shell>
  );
}
